import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DateInputFillMode, DateInputRounded, DateInputSize } from '@progress/kendo-angular-dateinputs';
import { CheckBoxRounded, InputSize } from '@progress/kendo-angular-inputs';
import { ENV_CONFIG } from 'src/app/core/injection.token';
import { NotificationService } from 'src/app/core/services/notification.service';
import { USER_ROLES } from 'src/app/extensions/constants/commonConstants';
import { EMAIL_VALIDATOR_REGEX } from 'src/app/extensions/constants/form-validator.constants';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { TenantExceptions } from 'src/app/extensions/services/api/exceptions';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { TenantAPIService } from 'src/app/extensions/services/api/tenant-api.service';
import { UserAPIService } from 'src/app/extensions/services/api/user-api.service';

@Component({
  selector: 'app-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss'],
  standalone: false,
})
export class UserDetailComponent implements OnInit {
  @ViewChild('form') public form: NgForm;

  user: app.models.UserModel = {
    isAdmin: false,
    acceptEmails: false,
    acceptSms: false,
    legalRepresentative: false,
    hasExternalProvider: false,
    dictionaryTwoFactorAuthenticationTypeId: null,
  } as app.models.UserModel;

  public size: InputSize = 'large';
  public rounded: CheckBoxRounded = 'large';

  loading: boolean = false;
  phoneValidator: app.models.Validator;
  emailValidator = EMAIL_VALIDATOR_REGEX;
  isEmailReadOnly: boolean = true;
  tenantConfiguration = { twoFactorAuthentication: undefined };
  enableLocalUser: boolean;
  isDisableExternalProviders: boolean = true;
  externalProviders: app.models.DictionaryDataModel[] = [];

  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';
  public sizeLarge: DateInputSize = 'large';
  public twoFactorAutenticationTypes: app.models.DictionaryDataModel[];

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _userAPIService: UserAPIService,
    private _router: Router,
    private _notificationService: NotificationService,
    private _translateService: TranslateService,
    private _partnerAPIService: PartnerAPIService,
    private _tenantAPIService: TenantAPIService,
    private _dictionaryAPIService: DictionaryAPIService,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData
  ) {
    this.enableLocalUser = JSON.parse(this._envConfig.ENABLE_LOCAL_USER || 'true');
  }

  ngOnInit() {
    this.user.userId = this._activatedRoute.snapshot.params['userId'];
    if (!this.user.userId) {
      this.isDisableExternalProviders = false;
      this.isEmailReadOnly = false;
    }
    this.user.externalTenantId = this._activatedRoute.snapshot.params['subTenantId'];
    this.loadData();
    this.setValidationRule();
  }

  setValidationRule() {
    this._tenantAPIService.getLocaleValidators().subscribe(res => {
      this.phoneValidator = res.find(x => x.code === 'MOBILE_PHONE');
    });
  }

  loadData() {
    if (this.user.userId) {
      this._userAPIService.getTenantUser(this.user.externalTenantId, this.user.userId).subscribe(user => {
        this.user = { ...this.user, ...user };
        if (user.externalProviderId) {
          this.user.hasExternalProvider = true;
        } else {
          this.isDisableExternalProviders = false;
        }
      });
    }
    if (this.user.externalTenantId) {
      this._partnerAPIService.getPartner(this.user.externalTenantId).subscribe(partner => {
        this.user.partnerName = partner.name;
        this.user.partnerUniqueIdentifier = partner.uniqueIdentifier;
      });
    }
    this._tenantAPIService.hasTenantTwoFactorAuthentication().subscribe(twoFactorAuth => {
      this.tenantConfiguration.twoFactorAuthentication = twoFactorAuth;
    });

    this._tenantAPIService.getExternalProviders(USER_ROLES.USER).subscribe(externalProviders => {
      this.externalProviders = externalProviders;
      if (!this.enableLocalUser && !this.user.hasExternalProvider) {
        this.user.hasExternalProvider = true;
        this.hasExternalProviderChange(true);
      }
    });

    this._dictionaryAPIService.getDictionaryTwoFactorAuthenticationTypes().subscribe(twoFactorTypes => {
      this.twoFactorAutenticationTypes = twoFactorTypes;
    });
  }

  saveUserHandler() {
    if (!this.form.valid) {
      return;
    }

    this.loading = true;
    this._userAPIService.saveUser(this.user).subscribe(response => {
      if (response.isSuccess) {
        this._notificationService.showSuccess(
          this._translateService.instant('users.add.notifications.userSaved'),
          this._translateService.instant('common.success')
        );
        this.form.resetForm();
        this.redirectUsers();
      } else {
        if (response.response.events.find(x => x.eventBody.exMessage == TenantExceptions.EmailAlreadyExists)) {
          this._notificationService.showWarning(
            this._translateService.instant('users.add.notifications.emailAlreadyExists'),
            this._translateService.instant('common.warning')
          );
        } else if (response.response.events.find(x => x.eventBody.exMessage == TenantExceptions.UserHasRoleAdmin)) {
          this._notificationService.showWarning(
            this._translateService.instant('users.add.notifications.userHasRoleAdmin'),
            this._translateService.instant('common.warning')
          );
        } else {
          this._notificationService.showError(
            this._translateService.instant('users.add.notifications.userSaveFailed'),
            this._translateService.instant('common.error')
          );
        }
      }
      this.loading = false;
    });
  }

  redirectUsers() {
    const navigationExtras = {
      state: { partnerName: this.user.partnerName, partnerId: this.user.externalTenantId },
    };
    this._router.navigate(['users'], navigationExtras);
  }

  previousExternalProvider: string = '';
  externalProviderChange(ev: any) {
    if (!this.enableLocalUser && this.previousExternalProvider === ev.currentTarget.id) {
      this.user.externalProviderId = this.previousExternalProvider;
    } else if (this.previousExternalProvider === ev.currentTarget.id) {
      this.user.externalProviderId = '';
    }
    this.previousExternalProvider = ev.currentTarget.id;
  }

  hasExternalProviderChange(hasExternalProvider: boolean) {
    if (hasExternalProvider) {
      this.user.externalProviderId = this.externalProviders[0].id;
    } else {
      this.user.externalProviderId = '';
    }
  }
}
